import { CursorType } from "frontend/canvas-designer-new/cursor-type";
import { Point } from "./math-utils";
import { MindmapNodeDirection, MindmapNodeElement } from "shared/datamodel/schemas/mindmap";
import { ElementDrawProps, NewElementData } from "shared/datamodel/canvas-element";
import consts, { TypeCanvasElement } from "shared/consts";
import { SubmenuDataUpdate } from "frontend/canvas-designer-new/canvas-submenu";
import { dbKey, getUnixTimestampUTC, isElementCountable, isElementNumberLimited } from "shared/util/utils";
import { IntegrationItem } from "shared/datamodel/schemas/integration-item";
import { placeIntegrationElement } from "shared/datamodel/integration-item";
import { IntegrationType } from "shared/integrations/integration";
import { CanvasElement, Connector } from "shared/datamodel/schemas";
import { SyncService } from "frontend/services/syncService";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { placeConnector } from "shared/datamodel/connector";
import Konva from "konva";
import { KonvaStage } from "frontend/konva-stage-types";

// Since we usually use the react API for posthog, and this it not a Reactive component,
// I used the vanulla web-based posthog API to get the flag.
// eslint-disable-next-line import/no-named-as-default
import posthog from "posthog-js";
import { placeMondayTask } from "shared/datamodel/monday-task";
import { MondayTaskElement } from "elements/monday-task/schema";

type CreateElementProps = {
  position: Point;
  type: CursorType;
  frameId?: string;
  size?: Point;
};

type CreateElementFunction = (props: CreateElementProps) => NewElementData[] | null;

export class CanvasUtils {
  createElement: CreateElementFunction;
  onAddElements: (elements: any[], addUndo?: boolean, source?: string, sequenceId?: string) => Promise<string[]>;
  setSelectedElementIds: (ids: string[] | ((previous: string[]) => string[])) => void;
  setEditingElementId: (id: string | null) => void;
  onElementsMutationEnded: (
    info: Map<string, ElementDrawProps>,
    undoInfo: Map<string, ElementDrawProps>,
    subMenuData?: SubmenuDataUpdate,
    addUndo?: boolean
  ) => void;
  onDeleteElements: (ids: string[], addUndo?: boolean, sequenceId?: string) => Promise<void>;
  onReplaceElements: (newIds: string[], oldIds: string[]) => void;
  copyElements: (elements: Map<string, any>, minPosition: Point, isTemplate: boolean) => Promise<string[] | undefined>;
  addMetadata: (metadata: { [id: string]: any }) => void;
  enableTransformer: () => void;

  constructor(
    createElement: CreateElementFunction,
    onAddElements: (elements: any[], addUndo?: boolean, source?: string, sequenceId?: string) => Promise<string[]>,
    setSelectedElementIds: (ids: string[] | ((previous: string[]) => string[])) => void,
    setEditingElementId: (id: string | null) => void,
    onElementsMutationEnded: CanvasUtils["onElementsMutationEnded"],
    onDeleteElements: (ids: string[], addUndo?: boolean, sequenceId?: string) => Promise<void>,
    onReplaceElements: (newIds: string[], oldIds: string[]) => void,
    copyElements: (
      elements: Map<string, any>,
      minPosition: Point,
      isTemplate: boolean
    ) => Promise<string[] | undefined>,
    addMetadata: (metadata: { [id: string]: any }) => void,
    enableTransformer: () => void
  ) {
    this.createElement = createElement;
    this.onAddElements = onAddElements;
    this.setSelectedElementIds = setSelectedElementIds;
    this.setEditingElementId = setEditingElementId;
    this.onElementsMutationEnded = onElementsMutationEnded;
    this.onDeleteElements = onDeleteElements;
    this.onReplaceElements = onReplaceElements;
    this.copyElements = copyElements;
    this.addMetadata = addMetadata;
    this.enableTransformer = enableTransformer;
  }

  addMindmapNode(parentId: string, rootId: string, side: MindmapNodeDirection, order: number, color: string) {
    const newElements = this.createElement({ position: { x: 0, y: 0 }, type: CursorType.mindmap });
    if (!newElements || newElements.length !== 1) {
      return;
    }
    const newElement = newElements[0];
    const node = newElement.element as MindmapNodeElement;
    node.rootId = rootId;
    node.parentId = parentId;
    node.direction = side;
    node.sortIndex = order;
    node.color = color;
    this.onAddElements([newElement]).then((ids) => {
      if (ids) {
        this.setSelectedElementIds(ids);
        this.setEditingElementId(ids[0]);
      }
    });
  }

  async addMindmapOrgChartNode(data: any) {
    const element = await this.onAddElements(data);
    return element;
  }

  addIntegrationItem(
    position: Point,
    integrationId: string,
    integrationType: IntegrationType,
    itemId: string,
    id?: string
  ) {
    const newMondayTaskEnabled = posthog.isFeatureEnabled("new-monday-task-element");
    let data: NewElementData;

    if (newMondayTaskEnabled) {
      const newElement = placeMondayTask(position, { mondayItemId: itemId, integrationId });
      data = {
        id: id ?? newElement.id,
        type: newElement.mondayTask.type,
        element: newElement.mondayTask as MondayTaskElement,
      };
    } else {
      const newElement = placeIntegrationElement(position, integrationId, integrationType, { itemId });
      data = {
        id: id ?? newElement.id,
        type: consts.CANVAS_ELEMENTS.INTEGRATION,
        element: newElement.item as IntegrationItem,
      };
    }

    this.onAddElements([data]).then((ids) => {
      if (ids) {
        this.setSelectedElementIds(ids);
      }
    });

    return `${data.type}-${data.id}`;
  }

  async addIntegrationItems<T extends CanvasElement>(
    items: { element: T; itemId: string; elementId: string }[],
    integrationId: string,
    integrationType: IntegrationType,
    syncService: SyncService<RW> | null,
    addUndo: boolean = true
  ) {
    const newMondayTaskEnabled = posthog.isFeatureEnabled("new-monday-task-element");

    const newElements: Record<string, { id: string; type: string; element: CanvasElement }> = {};
    const newConnectors: Record<string, { id: string; type: string; element: Connector }> = {};
    for (const item of items) {
      const { itemId, element, elementId } = item;
      const { x, y, attachedConnectors } = element;

      let newElement:
        | { id: string; mondayTask: MondayTaskElement }
        | { id: string; type: string; item: IntegrationItem };
      let newElementType: string;

      if (newMondayTaskEnabled) {
        newElement = placeMondayTask({ x, y }, { mondayItemId: itemId, integrationId, baseOnElement: element });
        newElementType = newElement.mondayTask.type;
      } else {
        newElement = placeIntegrationElement<T>({ x, y }, integrationId, integrationType, { itemId }, element);
        newElementType = newElement.type;
      }
      const newElementId = dbKey(newElement.id, newElementType);

      if (attachedConnectors) {
        for (const connectorId in attachedConnectors) {
          let connector = newConnectors[connectorId]?.element;
          // if we didn't replace the connector yet, get it from the db
          // we only add the new connector once to the new elements array
          if (!connector) {
            const dbConnector = (await syncService?.getElement(connectorId)) as Connector;
            connector = structuredClone(dbConnector); // make it mutable
          }

          // change old connector ids of the connected shapes to the new element id
          if (connector?.connectedShapes) {
            for (const [index, shape] of connector.connectedShapes.entries()) {
              if (shape?.id !== elementId) {
                continue;
              }
              connector.connectedShapes[index] = {
                id: newElementId,
                type: newElementType,
              };
              if (connector.anchorsMode && connector.anchorsMode[index] === "buttom" && !newMondayTaskEnabled) {
                // Old monday task elements only support "top" "left" "right" anchors mode
                connector.anchorsMode[index] = "top";
              }
            }
          }

          const newConnector = newConnectors[connectorId] || placeConnector({}, { x: 0, y: 0 });
          newConnector.element = connector;
          newConnectors[connectorId] = {
            id: newConnector.id,
            type: consts.CANVAS_ELEMENTS.CONNECTOR,
            element: newConnector.element,
          };

          // Replace the old connector id with the new one in the attached connectors of the element
          const newConnectorId = dbKey(newConnector.id, newConnector.type);
          const newAttachedConnectors = { ...attachedConnectors, [newConnectorId]: attachedConnectors[connectorId] };
          delete newAttachedConnectors[connectorId];

          if (newMondayTaskEnabled) {
            (newElement as { id: string; mondayTask: MondayTaskElement }).mondayTask.attachedConnectors =
              newAttachedConnectors;
          } else {
            (newElement as { id: string; type: string; item: IntegrationItem }).item.attachedConnectors =
              newAttachedConnectors;
          }
        }
      }

      newElements[elementId] = {
        id: newElement.id,
        type: newElementType,
        element: newMondayTaskEnabled
          ? (newElement as { id: string; mondayTask: MondayTaskElement }).mondayTask
          : (newElement as { id: string; type: string; item: IntegrationItem }).item,
      };
    }

    const allElements = Object.values(newElements).concat(Object.values(newConnectors));
    const oldIds = Object.keys(newElements).concat(Object.keys(newConnectors));

    const newIds = await this.onAddElements(allElements, addUndo);
    this.setSelectedElementIds(newIds);

    return { newIds, oldIds };
  }

  async removeCanvasElements(ids: string[], addUndo: boolean = true, sequenceId?: string) {
    await this.onDeleteElements(ids, addUndo, sequenceId);
  }

  replaceCanvasElements(newIds: string[], oldIds: string[]) {
    this.onReplaceElements(newIds, oldIds);
  }

  clearSelectedElementIds() {
    this.setSelectedElementIds([]);
    this.setEditingElementId(null);
  }

  moveToFrontNoUndo(id: string) {
    this.onElementsMutationEnded(new Map([[id, { zIndexLastChangeTime: getUnixTimestampUTC() }]]), new Map());
  }

  moveToBackNoUndo(id: string) {
    this.onElementsMutationEnded(new Map([[id, { zIndexLastChangeTime: -getUnixTimestampUTC() }]]), new Map());
  }
}

export const isElementChild = (element: CanvasElement | Konva.Node, parentElement: TypeCanvasElement) => {
  if (element instanceof Konva.Node) {
    return element.attrs?.element?.containerId?.includes(parentElement);
  }
  return element?.containerId?.includes(parentElement);
};

export function countCanvasStageElements(stageRef?: KonvaStage | null): number {
  return (stageRef?.getSelectableNodes((node) => isElementNumberLimited(node?.attrs?.element)) || []).length;
}

export function countCanvasStageElementsPerType(stageRef?: KonvaStage | null): Record<string, any> {
  const countPerElementType: Record<string, any> = {};
  const elements = stageRef?.getSelectableNodes((node) => isElementCountable(node?.attrs?.element)) ?? [];
  for (const element of elements) {
    const type = element?.attrs?.type;
    if (type) {
      countPerElementType[type] = (countPerElementType[type] ?? 0) + 1;
    }
  }
  return countPerElementType;
}
